/**
 * Admin · Requests
 * Faculty approval queue. Preserves EVERY existing business rule from
 * `OrdersManagement` in App.js:
 *   • Approve with return-timeline chip (non_returnable / 1w / 2w / 3w / 1m..4m)
 *   • Reject with mandatory reason
 *   • Read-only Student Kit Preview embedded per pending request
 *   • Soft ≥3-overdue nudge, non-blocking
 *   • Patient photo viewer (click "View Patient Photo")
 *
 * Reuses:
 *   GET  /api/orders
 *   PUT  /api/orders/{id}/status  { status: 'approved'|'rejected', reason?, non_returnable?, return_weeks?, return_months? }
 */
import React, { useMemo, useRef, useState } from 'react';
import { CheckCircle2, XOctagon, Image as ImageIcon, ClipboardCheck } from 'lucide-react';
import { apiClient } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import {
  Card, EmptyState, StatusPill, SearchInput, Modal, PhotoViewer,
  StudentKitPreview,
  orderStatusVariant, humanStatus,
  formatShortTime, shortRef,
} from '../ui';

/* Timeline choices — must match the mapping already implemented in the
 * legacy admin dashboard so business rules are preserved verbatim. */
const TIMELINE_OPTIONS = [
  { value: 'non_returnable', label: 'Non-returnable',        payload: { non_returnable: true } },
  { value: '1_week',         label: 'Return in 1 week',      payload: { return_weeks: 1 } },
  { value: '2_weeks',        label: 'Return in 2 weeks',     payload: { return_weeks: 2 } },
  { value: '3_weeks',        label: 'Return in 3 weeks',     payload: { return_weeks: 3 } },
  { value: '1_month',        label: 'Return in 1 month',     payload: { return_months: 1 } },
  { value: '2_months',       label: 'Return in 2 months',    payload: { return_months: 2 } },
  { value: '3_months',       label: 'Return in 3 months',    payload: { return_months: 3 } },
  { value: '4_months',       label: 'Return in 4 months',    payload: { return_months: 4 } },
];

const FILTERS = [
  { id: 'pending',   label: 'Pending' },
  { id: 'active',    label: 'Active' },
  { id: 'closed',    label: 'Closed' },
  { id: 'all',       label: 'All' },
];

const ACTIVE_STATUSES  = new Set(['approved', 'admin_approved', 'superadmin_approved', 'issued']);
const CLOSED_STATUSES  = new Set(['returned', 'completed', 'rejected', 'clerk_rejected', 'cancelled']);

export default function RequestsPage({ ctx, initialApproveOrderId, clearInitialApprove }) {
  const { allOrders, refresh, showToast } = ctx;

  const [scope, setScope] = useState('pending');
  const [studentQ, setStudentQ] = useState('');
  const [patientQ, setPatientQ] = useState('');
  const [orderQ,   setOrderQ]   = useState('');
  const [dateQ,    setDateQ]    = useState('');

  const [approvingOrder, setApprovingOrder] = useState(null);
  const [timeline, setTimeline] = useState('4_months');
  const [approving, setApproving] = useState(false);

  const [rejectingOrder, setRejectingOrder] = useState(null);
  const [rejectReason, setRejectReason]   = useState('');
  const [rejecting, setRejecting] = useState(false);

  const [photoUrl, setPhotoUrl] = useState(null);
  const activeRequest = useRef(null);

  const filtered = useMemo(() => {
    const s = studentQ.trim().toLowerCase();
    const p = patientQ.trim().toLowerCase();
    const o = orderQ.trim().toLowerCase();
    return (allOrders || []).filter((order) => {
      // Scope filter
      const st = (order.status || '').toLowerCase();
      if (scope === 'pending' && st !== 'pending') return false;
      if (scope === 'active'  && !ACTIVE_STATUSES.has(st)) return false;
      if (scope === 'closed'  && !CLOSED_STATUSES.has(st)) return false;
      // Text filters (parity with legacy)
      if (s && !(order.student_name || '').toLowerCase().includes(s)) return false;
      if (p && !(order.patient_info?.name || '').toLowerCase().includes(p)) return false;
      if (o && !order.id.slice(-8).toLowerCase().includes(o)) return false;
      if (dateQ && new Date(order.created_at).toISOString().split('T')[0] !== dateQ) return false;
      return true;
    });
  }, [allOrders, scope, studentQ, patientQ, orderQ, dateQ]);

  const totalQty = (order) => (order?.items || []).reduce((s, it) => s + (it.quantity || 0), 0);

  const openApprove = (order) => {
    activeRequest.current = order.id;
    setApprovingOrder(order);
    setTimeline('4_months');
  };

  // Tap-through from the notification bell: if the parent asked us to open
  // the approve modal for a specific order, do it as soon as that order is
  // present in `allOrders`. Clears itself so it never re-fires.
  React.useEffect(() => {
    if (!initialApproveOrderId) return;
    const target = (allOrders || []).find((o) => o.id === initialApproveOrderId && (o.status || '').toLowerCase() === 'pending');
    if (target) {
      setScope('pending');
      setApprovingOrder(target);
      setTimeline('4_months');
      activeRequest.current = target.id;
      if (clearInitialApprove) clearInitialApprove();
    }
  }, [initialApproveOrderId, allOrders, clearInitialApprove]);

  const submitApprove = async () => {
    if (!approvingOrder || approving) return;
    const opt = TIMELINE_OPTIONS.find((x) => x.value === timeline);
    if (!opt) return;
    setApproving(true);
    try {
      await apiClient.put(endpoints.orders.statusUpdate(approvingOrder.id), {
        status: 'approved',
        ...opt.payload,
      });
      showToast(`Approved ${shortRef(approvingOrder.id, 'REQ')} · ${opt.label.toLowerCase()}`, 'ok');
      setApprovingOrder(null);
      await Promise.all([refresh.orders(), refresh.pending(), refresh.overdue()]);
    } catch (e) {
      showToast(e?.response?.data?.detail || 'Approval failed.', 'bad');
    } finally {
      setApproving(false);
    }
  };

  const openReject = (order) => {
    activeRequest.current = order.id;
    setRejectingOrder(order);
    setRejectReason('');
  };

  const submitReject = async () => {
    if (!rejectingOrder || rejecting) return;
    if (!rejectReason.trim()) {
      showToast('Please provide a reason before rejecting.', 'bad');
      return;
    }
    setRejecting(true);
    try {
      await apiClient.put(endpoints.orders.statusUpdate(rejectingOrder.id), {
        status: 'rejected',
        reason: rejectReason.trim(),
      });
      showToast(`Rejected ${shortRef(rejectingOrder.id, 'REQ')} · reserved stock restored`, 'ok');
      setRejectingOrder(null);
      setRejectReason('');
      await Promise.all([refresh.orders(), refresh.pending()]);
    } catch (e) {
      showToast(e?.response?.data?.detail || 'Rejection failed.', 'bad');
    } finally {
      setRejecting(false);
    }
  };

  const openPatientPhoto = (raw) => {
    if (!raw) return;
    const url = raw.startsWith('http') ? raw : `${process.env.REACT_APP_BACKEND_URL}${raw}`;
    setPhotoUrl(url);
  };

  const clearFilters = () => {
    setStudentQ(''); setPatientQ(''); setOrderQ(''); setDateQ('');
  };

  return (
    <div data-testid="ap-requests-page">
      <div className="ap-eyebrow">Faculty Queue</div>
      <h1 className="ap-page-title">Requests</h1>
      <p className="ap-page-lede">
        Approve or reject requests directed to you. Every pending card shows the student's
        current kit so you can weigh outstanding returnables before you decide.
      </p>

      {/* Filters */}
      <Card className="ap-mt-5" testId="ap-requests-filters">
        <div className="ap-filter-row">
          <div>
            <label className="ap-field-label" htmlFor="ap-req-student">Student</label>
            <SearchInput
              value={studentQ}
              onChange={setStudentQ}
              placeholder="Student name…"
              testId="ap-filter-student"
            />
          </div>
          <div>
            <label className="ap-field-label" htmlFor="ap-req-patient">Patient</label>
            <input
              id="ap-req-patient"
              type="text"
              className="ap-input"
              value={patientQ}
              onChange={(e) => setPatientQ(e.target.value)}
              placeholder="Patient name…"
              data-testid="ap-filter-patient"
            />
          </div>
          <div>
            <label className="ap-field-label" htmlFor="ap-req-order">Order</label>
            <input
              id="ap-req-order"
              type="text"
              className="ap-input"
              value={orderQ}
              onChange={(e) => setOrderQ(e.target.value)}
              placeholder="Last 8 chars"
              data-testid="ap-filter-order"
            />
          </div>
          <div>
            <label className="ap-field-label" htmlFor="ap-req-date">Date</label>
            <input
              id="ap-req-date"
              type="date"
              className="ap-input"
              value={dateQ}
              onChange={(e) => setDateQ(e.target.value)}
              data-testid="ap-filter-date"
            />
          </div>
          <div>
            <button
              type="button"
              className="ap-btn ap-btn-ghost"
              onClick={clearFilters}
              data-testid="ap-filter-clear"
            >Clear</button>
          </div>
        </div>

        <div className="ap-chip-row" style={{ marginTop: 8 }}>
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              className={`ap-chip${scope === f.id ? ' is-active' : ''}`}
              onClick={() => setScope(f.id)}
              data-testid={`ap-scope-${f.id}`}
            >{f.label}</button>
          ))}
        </div>
      </Card>

      {/* Request cards */}
      {filtered.length === 0 ? (
        <div className="ap-mt-5">
          <EmptyState
            icon={ClipboardCheck}
            title={scope === 'pending' ? 'No pending requests' : 'No requests match'}
            message={scope === 'pending'
              ? 'When a student submits a request directed to you it will appear here.'
              : 'Try loosening the filters or switching scope.'}
            testId="ap-requests-empty"
          />
        </div>
      ) : (
        <div className="ap-req-list ap-mt-5" data-testid="ap-requests-list">
          {filtered.map((order) => {
            const isPending = (order.status || '').toLowerCase() === 'pending';
            return (
              <article
                key={order.id}
                className={`ap-req-card${isPending ? ' is-pending' : ''}`}
                data-testid={`ap-req-card-${order.id}`}
              >
                <header className="ap-req-head">
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <h3 className="ap-req-title">
                      {order.student_name || 'Unknown student'}
                      {order.patient_info?.name && (
                        <>
                          {' '}
                          <span style={{ color: 'var(--diis-ink-subtle)', fontSize: 15, fontWeight: 400 }}>
                            for
                          </span>{' '}
                          {order.patient_info.name}
                        </>
                      )}
                    </h3>
                    <div className="ap-req-meta">
                      <span><strong>{shortRef(order.id, 'REQ')}</strong></span>
                      <span>Total qty · <strong>{totalQty(order)}</strong></span>
                      <span>Created · {order.formatted_created_at || formatShortTime(order.created_at)}</span>
                      {order.formatted_admin_approved_at && (
                        <span>Approved · {order.formatted_admin_approved_at}</span>
                      )}
                      {order.patient_info?.photo_url && (
                        <button
                          type="button"
                          className="ap-req-patient-photo-btn"
                          onClick={() => openPatientPhoto(order.patient_info.photo_url)}
                          data-testid={`ap-view-patient-photo-${order.id}`}
                        >
                          <ImageIcon size={11} style={{ verticalAlign: -1, marginRight: 4 }} />
                          View Patient Photo
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="ap-req-actions">
                    <StatusPill variant={orderStatusVariant(order.status)}>
                      {humanStatus(order.status)}
                    </StatusPill>
                    {isPending && (
                      <>
                        <button
                          type="button"
                          className="ap-btn ap-btn-ghost ap-btn-sm"
                          onClick={() => openReject(order)}
                          data-testid={`ap-req-reject-${order.id}`}
                        >Reject</button>
                        <button
                          type="button"
                          className="ap-btn ap-btn-accent ap-btn-sm"
                          onClick={() => openApprove(order)}
                          data-testid={`ap-req-approve-${order.id}`}
                        >
                          Approve
                        </button>
                      </>
                    )}
                  </div>
                </header>

                <ul className="ap-req-items">
                  {(order.items || []).map((it, i) => (
                    <li key={i}>
                      <span className="ap-item-qty">×{it.quantity}</span>
                      <strong>{it.component_name}</strong>
                      {it.brand_name && <span className="ap-item-brand">{it.brand_name}</span>}
                      {it.is_returnable && <StatusPill variant="returnable">Returnable</StatusPill>}
                    </li>
                  ))}
                </ul>

                {order.notes && (
                  <div className="ap-req-note" data-testid={`ap-req-notes-${order.id}`}>
                    <strong style={{ color: 'var(--diis-ink)', fontWeight: 500 }}>Notes: </strong>
                    {order.notes}
                  </div>
                )}
                {order.reason && (
                  <div className="ap-req-reason">
                    <strong style={{ color: '#8A3A2E', fontWeight: 600 }}>Reason: </strong>
                    {order.reason}
                  </div>
                )}

                {/* Faculty Kit-Aware Preview — only on pending requests */}
                {isPending && order.student_id && (
                  <StudentKitPreview
                    studentId={order.student_id}
                    studentName={order.student_name || 'this student'}
                  />
                )}
              </article>
            );
          })}
        </div>
      )}

      {/* Approve modal */}
      <Modal
        open={!!approvingOrder}
        onClose={() => !approving && setApprovingOrder(null)}
        title="Approve Request"
        subtitle="Confirm the return timeline. This becomes the student's return-by policy."
        wide
        testId="ap-approve-modal"
        footer={(
          <>
            <button
              type="button"
              className="ap-btn ap-btn-ghost"
              onClick={() => setApprovingOrder(null)}
              disabled={approving}
              data-testid="ap-approve-cancel"
            >Cancel</button>
            <button
              type="button"
              className="ap-btn ap-btn-accent"
              onClick={submitApprove}
              disabled={approving || !timeline}
              data-testid="ap-approve-submit"
            >
              <CheckCircle2 size={15} /> {approving ? 'Approving…' : 'Confirm Approve'}
            </button>
          </>
        )}
      >
        {approvingOrder && (
          <>
            <dl className="ap-def-list">
              <div><dt>Student</dt><dd>{approvingOrder.student_name}</dd></div>
              <div><dt>Request</dt><dd className="ap-mono-num">{shortRef(approvingOrder.id, 'REQ')}</dd></div>
              <div><dt>Patient</dt><dd>{approvingOrder.patient_info?.name || '—'}</dd></div>
              <div><dt>Total Quantity</dt><dd className="ap-mono-num">{totalQty(approvingOrder)}</dd></div>
            </dl>

            <div className="ap-mt-5">
              <label className="ap-field-label">Return timeline</label>
              <div className="ap-chip-row">
                {TIMELINE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`ap-chip${timeline === opt.value ? ' is-active' : ''}`}
                    onClick={() => setTimeline(opt.value)}
                    data-testid={`ap-timeline-${opt.value}`}
                  >{opt.label}</button>
                ))}
              </div>
              <p className="ap-subtle ap-mt-3">
                Choosing <em style={{ fontStyle: 'normal' }}>Non-returnable</em> marks every item in this request as consumable.
              </p>
            </div>
          </>
        )}
      </Modal>

      {/* Reject modal */}
      <Modal
        open={!!rejectingOrder}
        onClose={() => !rejecting && setRejectingOrder(null)}
        title="Reject Request"
        subtitle="Reserved stock is automatically restored. The student will see your reason."
        testId="ap-reject-modal"
        footer={(
          <>
            <button
              type="button"
              className="ap-btn ap-btn-ghost"
              onClick={() => setRejectingOrder(null)}
              disabled={rejecting}
              data-testid="ap-reject-cancel"
            >Cancel</button>
            <button
              type="button"
              className="ap-btn ap-btn-danger"
              onClick={submitReject}
              disabled={rejecting || !rejectReason.trim()}
              data-testid="ap-reject-submit"
            >
              <XOctagon size={15} /> {rejecting ? 'Rejecting…' : 'Confirm Reject'}
            </button>
          </>
        )}
      >
        {rejectingOrder && (
          <>
            <dl className="ap-def-list">
              <div><dt>Student</dt><dd>{rejectingOrder.student_name}</dd></div>
              <div><dt>Request</dt><dd className="ap-mono-num">{shortRef(rejectingOrder.id, 'REQ')}</dd></div>
            </dl>
            <div className="ap-mt-5">
              <label className="ap-field-label" htmlFor="ap-reject-reason">Reason</label>
              <textarea
                id="ap-reject-reason"
                className="ap-textarea"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="e.g. Requested quantity exceeds ward's monthly cap; please resubmit with fewer units."
                data-testid="ap-reject-reason"
                autoFocus
              />
            </div>
          </>
        )}
      </Modal>

      {/* Patient photo viewer */}
      <PhotoViewer src={photoUrl} onClose={() => setPhotoUrl(null)} />
    </div>
  );
}
